import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import SharedForm from "@widgets/SharedForm";
import FreeForm from "@widgets/FreeForm";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import { showDialog } from "@widgets/CommonDialog";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_SFA_JOURNAL_REG_001_POP_01 from "./SN_SFA_JOURNAL_REG_001_POP_01";
import SN_SFA_JOURNAL_REG_001_POP_02 from "./SN_SFA_JOURNAL_REG_001_POP_02";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getCodeData, getInitFormData, getNoSlip } from "@shared/utils/CommonUtils";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { global } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";

dayjs.extend(isoWeek);

interface Props {
  Menuprops: menuInfo;
}

/** 업무번호 채번 정의(sys_slip) — cd_module=SFA, no_line=15, cd_prefix=JURNAL(주간회의록), qt_serial=4 */
const NO_SLIP_MODULE = "SFA";
const NO_SLIP_LINE = 15;

/**
 * 기준주차의 월요일(YYYYMMDD).
 * 헤더 일자이자 채번 기준일이라, 이 값으로 그 주차가 속한 연월로 번호가 만들어진다.
 * (1월 4일은 ISO 규칙상 항상 그 해 1주차라 거기서 주차를 옮긴다.)
 */
const weekMonday = (yy?: string, no_week?: string) => {
  const year = Number(String(yy ?? "").substring(0, 4));
  const week = Number(no_week);
  if (!year || !week) return dayjs().format("YYYYMMDD");
  return dayjs(`${year}-01-04`)
    .isoWeek(week)
    .startOf("isoWeek")
    .format("YYYYMMDD");
};

/** 주간일지 라인 구분: 1=전주실적, 2=차주계획 (sfa_workjournall.fg_plan) */
const FG_ACTUAL = "1";
const FG_PLAN = "2";
/** 헤더 상태값(st_journal) — 분류코드 SFA047: 1=신규, 2=Draft, 3=제출 */
const ST_NEW = "1";
const ST_DRAFT = "2";
const ST_SUBMIT = "3";
/** 검색패널 '상태'는 읽기전용 표시칸이라 코드가 아니라 이름을 넣는다(저장값은 headerData 쪽 코드). */
const ST_LABELS: Record<string, string> = {
  [ST_NEW]: "신규",
  [ST_DRAFT]: "Draft",
  [ST_SUBMIT]: "제출",
};
/** 실적이월 시 차주계획 행에 표시할 이월여부 컬럼 */
const COL_CARRY = "yn_carryover";
/**
 * 긴급도(no_priority) 신규 행 기본값 — 보통.
 * 상세폼의 SnStepUp 은 행 데이터(formData[cd_param])만 보고 `sys_formctrl.cd_value` 는 참조하지 않아
 * (SnFindFormContols 의 SnStepUp 분기), 신규 행 값은 화면에서 채워야 한다.
 * 값은 분류코드(cd_clas)에 등록된 코드와 정확히 같아야 한다 — 다르면 이름도 색도 안 나온다.
 */
const NO_PRIORITY_NORMAL = "2";
/**
 * ACTIVITY 분류코드. 상세폼 콤보는 코드(cd_activity)만 바꾸는데
 * 그리드 ACTIVITY 컬럼은 이름(nm_activity)을 그리므로, 코드가 바뀌면 이름도 같이 채워야 한다.
 * (조회 SQL 은 mas_codel 같은 코드로 조인해서 이름을 내려준다.)
 */
const CD_CLAS_ACTIVITY = "SFA046";
/**
 * 상세폼 grid 열 템플릿. SharedForm 기본값(13열 고정폭)을 쓰면 이 화면의 좁은 분할 패널에서
 * 레이아웃이 깨져서, 열을 지정하지 않고 콘텐츠(colspan)에 따라 자동 배치되도록 none 을 넘긴다.
 */
const FORM_COLUMNS = "none";
/**
 * 상단 SearchPanel 열 템플릿. 공통 기본값(minmax(85px, 1fr))이면 기준주차(SnYearWeekPicker,
 * 약 417px)가 qt_colspan 보다 넓을 때 옆 칸 라벨 위로 삐져나와서, 이 화면만 min-content 로 둔다.
 */
const SEARCH_COLUMNS = "repeat(12, minmax(min-content, 1fr))";
/**
 * 주간요약 패널 위 여백(px). 좌측 그리드 헤더에만 버튼이 있어 우측 컬럼이 그만큼 짧다.
 * 좌우 바닥이 어긋나 보이면 이 값만 조절한다.
 */
const SUMMARY_TOP_GAP = 3;
/**
 * 좌우 그리드 제목줄 높이(px).
 * 좌측에만 버튼이 있으면 제목줄 높이가 달라져 우측 +/휴지통 아이콘이 위로 밀리고
 * 상단 구분선이 끊겨 보인다. 양쪽에 같은 높이를 강제해 선이 이어지게 한다.
 * 선이 여전히 어긋나면 이 값만 조절한다(SnButton 실측 약 31px).
 */
const GRID_HEADER_HEIGHT = 31;
/** 그리드 내부 관리용 키 — 저장/복사 대상에서 제외한다. */
const GRID_KEYS = [
  "KeyField",
  "state",
  "block",
  "fixCheckbox",
  "st_header",
  // ⚠ cd_emp / nm_emp 를 여기 넣지 말 것 —
  // stripGridKeys 는 라인뿐 아니라 buildHeaderRow(헤더 저장)에서도 쓰인다.
  // 넣으면 헤더의 담당자가 지워져 cd_emp = '' 로 저장되고, 그 일지는 조회 조건에 잡히지 않는다.
  // 라인 payload 에 실려도 sfa_workjournall DTO 에 없는 키라 무시된다(nm_cust 등과 동일).
  // 이슈 조인 표시용 — 원본은 sfa_issue 라 일지에는 no_issue 만 저장한다.
  "nm_fg_item",
  "nm_tp_issue1",
  "nm_tp_issue2",
  "nm_st_issue",
  "nm_emp_issue",
  "dt_due",
  // 이슈번호 표시용 — 값이 없으면 "직접입력" 으로 보여 주는 칸이라 저장 대상이 아니다.
  // 저장은 no_issue(빈 값 = 직접 추가)만 한다.
  "nm_issue_disp",
];
/**
 * 업무영역 — 1000 컨설팅 · 2000 개발 · 3000 영업.
 * 개발일 때만 이슈 기반 레이아웃(grid3/grid4, freeform4/freeform5)을 쓴다.
 * (분류코드 SFA054 는 폐기됐다. 콤보 목록은 sys_formctrl.dc_sql 이 직접 조회한다 —
 *  값을 바꾸려면 mas_codel 이 아니라 그 SQL 을 고쳐야 하고, 이 상수도 함께 맞춰야 한다.)
 */
const FG_DOMAIN_DEV = "2000";
/**
 * 조회 쿼리. 결과셋 3개(헤더/전주실적/차주계획). 개발 업무영역의 이슈(sfa_issue) 조인 컬럼도
 * 이 쿼리에 포함돼 있어, 업무영역과 무관하게 이 하나만 쓴다.
 * (withHeaderState·syncSearchPanel·저장 payload 가 이 컬럼명을 전제로 동작한다.)
 */
const QUERY_ID = "sn_sfa_journal_reg_001_01";

const stripGridKeys = (row: any) => {
  const copy = { ...row };
  GRID_KEYS.forEach((key) => delete copy[key]);
  return copy;
};

/**
 * 참조업무는 화면에 칸이 하나뿐이라 "업무번호-항번" 으로 합쳐서 보여준다(조회 SQL 과 같은 규칙).
 * 항번이 없으면(0) 업무번호만 쓴다.
 */
const joinRef = (no_journal_ref?: string, no_line_ref?: any) => {
  if (!no_journal_ref) return "";
  const no_line = Number(no_line_ref) || 0;
  return no_line ? `${no_journal_ref}-${no_line}` : no_journal_ref;
};

/** 저장 직전 합쳐진 참조업무를 원래 두 컬럼으로 되돌린다. DB 에는 합친 문자열이 들어가면 안 된다. */
const splitRef = (row: any) => {
  const ref = String(row.no_journal_ref ?? "");
  const at = ref.lastIndexOf("-");
  if (at < 0) return row;
  return {
    ...row,
    no_journal_ref: ref.slice(0, at),
    no_line_ref: Number(ref.slice(at + 1)) || 0,
  };
};

const emptyChange = { insert: [], update: [], delete: [], modifiedRowcount: 0 };

// 드래그 재정렬은 no_seq_sort 를 바꿔 저장한다(rowDragOrderKey). SnGrid 가 그 변경을
// getChange().update 로 잡아 주므로(setDataValue "noEvent" 도 수정목록 포함), 화면에서
// no_line 을 다시 매기는 별도 재정렬 처리는 필요 없다.

const SN_SFA_JOURNAL_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region Variable
  const searchRef = useRef<any>(null);
  const gridref1 = useRef<any>(null); // 금주실적 (fg_plan = 1)
  const gridref2 = useRef<any>(null); // 차주계획 (fg_plan = 2)
  const detailRef1 = useRef<any>(null);
  const detailRef2 = useRef<any>(null);
  const summaryRef = useRef<any>(null); // 주간총평/주요이슈/지원요청/중점사항
  const { userInfo } = global();
  //#endregion

  //#region Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColumns1] = useState<any[]>([]);
  const [columns2, setColumns2] = useState<any[]>([]);
  const [detailForm1, setDetailForm1] = useState<any[]>([]);
  const [detailForm2, setDetailForm2] = useState<any[]>([]);
  const [summaryForm, setSummaryForm] = useState<any[]>([]);
  /** 현재 레이아웃 기준 업무영역. 검색조건 변경 즉시 / 조회 시 헤더 값으로 갱신된다. */
  const [fgDomain, setFgDomain] = useState<string>("");
  /**
   * 담당자를 지정하지 않고 조회하면 여러 사람의 일지가 섞이므로 그리드에 담당자 컬럼을 보여 준다.
   * (지정 조회 때는 모든 행이 같은 사람이라 컬럼이 노이즈다.)
   */
  const [isAllEmpSearch, setIsAllEmpSearch] = useState(false);
  const [dataSource1, setDataSource1] = useState<any[]>([]);
  const [dataSource2, setDataSource2] = useState<any[]>([]);
  const [focusedRow1, setFocusedRow1] = useState<any>();
  const [focusedRow2, setFocusedRow2] = useState<any>();
  const [headerData, setHeaderData] = useState<any>();
  // 조회된 헤더. 신규 직전 판단에 쓰는데 state는 즉시 반영되지 않아 ref로 들고 있다.
  // insert/update 판단은 저장 시점에 다시 조회해서 정한다(fetchHeader).
  const headerRef = useRef<any>(null);
  //#endregion

  //#region grid 길이 관련
  const cd_menu = Menuprops.cd_menu || "SN_SFA_JOURNAL_REG_001";

  const defaultSize = {
    // 좌/우 세로 합계를 220%로 맞춘다(좌 30+135, 우 30+80+60).
    panelLeft: "50%", // seq 1 : 좌측(금주실적) 폭
    panelActual: "30%", // seq 2 : 금주실적 그리드 높이
    // seq 3, 5(상세), 6(주간요약)은 fitContent 라 높이값을 쓰지 않는다. usePanelSizes 가
    // seq → 키 순서로 매핑하므로 지우면 seq 4 가 어긋난다. 자리만 남겨 둔다.
    panelActualDetail: "167%", // seq 3 : (미사용)
    panelPlan: "30%", // seq 4 : 차주계획 그리드 높이
    panelPlanDetail: "110%", // seq 5 : (미사용)
    panelWeeklyRecap: "60%", // seq 6 : (미사용)
  };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  /**
   * 메타 원본. 업무영역에 따라 쓰는 세트가 달라지므로 통째로 들고 있다가 골라 쓴다.
   * (getInitForm 은 탭을 열 때 한 번만 호출된다.)
   */
  const initMeta = useRef<any>(null);

  /**
   * 업무영역별 메타 세트 적용.
   * 개발(2000)은 이슈 기반 레이아웃(grid3/grid4 · freeform4/freeform5),
   * 영업·컨설팅·미정은 기존 레이아웃(grid1/grid2 · freeform1/freeform2).
   * 주간요약(freeform3)은 업무영역과 무관하게 공용이다.
   */
  const applyDomainMeta = useCallback((fg_domain?: string) => {
    const domain = String(fg_domain ?? "");
    // 이슈 가져오기 버튼 노출도 같은 값을 기준으로 한다(레이아웃과 어긋나지 않게).
    setFgDomain(domain);

    const meta = initMeta.current;
    if (!meta) return;
    const isDevDomain = domain === FG_DOMAIN_DEV;
    setColumns1(meta.grid?.[isDevDomain ? "grid3" : "grid1"] || []);
    setColumns2(meta.grid?.[isDevDomain ? "grid4" : "grid2"] || []);
    setDetailForm1(meta.form?.[isDevDomain ? "freeform4" : "freeform1"] || []);
    setDetailForm2(meta.form?.[isDevDomain ? "freeform5" : "freeform2"] || []);
  }, []);

  /**
   * 검색조건 변경 후크. 업무영역을 바꾸면 조회를 누르기 전에 레이아웃을 먼저 바꾼다.
   * (조회하면 헤더의 fg_domain 으로 한 번 더 맞춘다 — 저장된 일지의 업무영역이 기준이다.)
   */
  /**
   * 담당자 컬럼(nm_emp)만 표시/숨김을 바꿔 넘긴다. 메타에는 숨김(fg_visible='N')으로 등록해 두고
   * 담당자를 비우고 조회했을 때만 켠다.
   */
  const withEmpColumn = (columns: any[], show: boolean) =>
    columns.map((column) =>
      column.cd_column === "nm_emp"
        ? { ...column, fg_visible: show ? "Y" : "N" }
        : column
    );

  const onSearchValueChanged = useCallback(
    (event: any) => {
      if (event?.colId !== "fg_domain") return;
      applyDomainMeta(event.newValue);
      // 한 번 조회한 뒤라면 바로 다시 조회한다 — 레이아웃만 바뀌고 이전 업무영역 데이터가 남는 상태를 막는다.
      // (검색패널은 콜백보다 먼저 자기 formData 를 갱신하므로 onSearch 가 바뀐 업무영역을 읽는다.)
      if (searched.current) void onSearchRef.current?.();
    },
    [applyDomainMeta]
  );

  /** 조회를 한 번이라도 했는지. 화면을 열자마자 업무영역만 바꿨을 때는 자동 조회하지 않는다. */
  const searched = useRef(false);
  /**
   * 자동 조회용 최신 onSearch. 검색패널이 onValueChanged 를 자기 렌더 시점 기준으로 잡아 두기 때문에
   * 프롭을 직접 부르면 오래된 클로저(옛 state)를 쓸 수 있다.
   */
  const onSearchRef = useRef<(() => Promise<boolean | undefined>) | null>(null);

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        // SnCodeBox 는 코드만으로 이름을 조회하지 않으므로 표시용 nm_emp 도 같이 넣는다.
        setFormData({
          ...(rtn.formData ?? {}),
          cd_emp: rtn.formData?.cd_emp || userInfo.cd_emp || "",
          nm_emp: rtn.formData?.nm_emp || userInfo.nm_emp || "",
        });
        setFormControlData(rtn.formControlData);
        initMeta.current = rtn;
        applyDomainMeta(rtn.formData?.fg_domain);
        setSummaryForm(rtn.form?.freeform3 || []);
      } catch (error) {}
    };
    fetchform();
  }, [applyDomainMeta]);

  // 주간업무보고(REP_001) 전주실적/금주계획 행 더블클릭 드릴다운 진입.
  // 넘어온 조건(주차/담당자/업무영역)으로 검색패널을 채우고 조회한다.
  // Menuprops 의존이라 이미 열려 있는 탭이면 조건만 바뀌어 재조회된다.
  const onDrilldownActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops as any).figure !== "fg_drilldown.Search") return;
    const current = searchRef.current?.getFormdata?.() ?? {};
    const merged = {
      ...current,
      yy: nodeData.yy,
      no_week: nodeData.no_week,
      cd_emp: nodeData.cd_emp,
      nm_emp: nodeData.nm_emp,
      fg_domain: nodeData.fg_domain,
      st_journal: nodeData.st_journal,
    };
    // formDataRef 즉시 동기화(onSearch/필수검증용) + 화면상태로 검색패널 표시 갱신 + 업무영역 메타 적용
    searchRef.current?.setFormdata?.(merged);
    setFormData(merged);
    applyDomainMeta(nodeData.fg_domain);
    await onSearch();
  };

  useEffect(() => {
    if (
      (Menuprops as any).figure === "fg_drilldown.Search" &&
      formControlData.length > 0
    ) {
      onDrilldownActivate((Menuprops as any).nodeData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formControlData, Menuprops]);

  // ACTIVITY 코드 → 이름. 상세폼 콤보가 코드만 주므로 이름은 여기서 찾아 넣는다.
  const activityNames = useRef<Record<string, string>>({});
  useEffect(() => {
    const fetchActivityNames = async () => {
      try {
        const rows: any[] = (await getCodeData(CD_CLAS_ACTIVITY, "")) ?? [];
        rows.forEach((row) => {
          activityNames.current[String(row.code)] = row.name;
        });
      } catch (error) {}
    };
    fetchActivityNames();
  }, []);

  /**
   * 상세폼 값 변경 후크. 여기서 돌려준 값이 폼과 그리드 행에 함께 반영된다(SharedForm).
   * 코드만 바뀌는 컨트롤의 이름 컬럼을 채워, 저장 전에도 그리드가 옛 이름을 물고 있지 않게 한다.
   */
  const onDetailValueChanged = useCallback((data: any, param: string) => {
    if (param !== "cd_activity") return {};
    return { nm_activity: activityNames.current[String(data ?? "")] ?? "" };
  }, []);

  /**
   * 화면 잠금/버튼 상태는 헤더(st_journal)가 기준이다.
   * 라인의 st_journal 은 표시용 이름이 들어 있어 판단 근거로 쓸 수 없다.
   * 신규·Draft 는 작성 단계라 열어두고, 제출된 건만 잠근다.
   * 값이 비어 있으면(신규 작성 시작 전) 잠그지 않는다.
   */
  const stJournal = headerData?.st_journal ?? "";
  const isSubmitted = stJournal === ST_SUBMIT;
  /** 로그인 사용자의 사원코드. 비어 있는 계정(관리자 등)에는 담당자 기준 잠금을 적용하지 않는다. */
  const myEmp = String(userInfo.cd_emp ?? "");
  /**
   * 보기 전용 — 내 일지가 아니면 쓰지 못한다.
   *   · 담당자 미지정 조회(여러 사람의 일지가 섞임)
   *   · 담당자가 로그인 사용자가 아닌 일지(남의 일지)
   * headerData 는 조회된 헤더이고, 헤더가 없으면 조회조건 값이 들어 있다(onSearch 의 setHeaderData).
   */
  const isViewOnly =
    isAllEmpSearch ||
    (!!myEmp && !!headerData && String(headerData.cd_emp ?? "") !== myEmp);
  /**
   * 입력 잠금 규칙 — 아래 세 경우에 화면이 보기 전용이 된다.
   *   1) 제출됨(st_journal = 3)  : 확정된 일지라 수정하면 안 된다.
   *   2) 담당자 미지정 조회       : 여러 사람의 일지가 섞여 나오므로 어느 일지에 쓰는지 특정할 수 없다.
   *   3) 남의 일지               : 담당자가 로그인 사용자가 아니면 쓰지 못한다(isViewOnly).
   *
   * 막는 지점(하나라도 빠지면 그 경로로 입력이 새어 나간다):
   *   - 그리드 셀 편집      : SnGrid etcProps.isEditable = !isLocked
   *   - 행 추가·삭제 아이콘 : 핸들러를 넘기지 않는다 → SnTabHeader 가 아이콘 자체를 안 그린다
   *   - 가져오기·이월 버튼  : disabled={isLocked}
   *   - 상세폼·주간요약     : st_journal 에 제출 코드를 실어 메타의 dc_editcon 으로 잠근다
   *                          (withHeaderState / summaryData — 폼은 isLocked 를 직접 보지 않는다)
   *   - 툴바 신규·저장·삭제 : useImperativeHandle 에서 제외 + onSave 진입 가드(다른 호출 경로 대비)
   *   - 제출/제출취소 버튼  : disabled={isViewOnly} + onSubmit 진입 가드
   *                          (isLocked 로 막으면 제출된 내 건의 '제출취소'까지 막힌다)
   */
  const isLocked = isSubmitted || isViewOnly;
  /**
   * 개발 업무영역 = 이슈 기반 레이아웃. 이슈 가져오기도 이때만 노출한다(다른 영역은 이슈 컬럼이 없다).
   * 기준값은 applyDomainMeta 가 넣어 주는 fgDomain — 검색조건을 바꾸는 즉시 레이아웃과 함께 갱신된다.
   */
  const isDev = fgDomain === FG_DOMAIN_DEV;

  const gridColumns1 = useMemo(
    () => withEmpColumn(columns1, isAllEmpSearch),
    [columns1, isAllEmpSearch]
  );
  const gridColumns2 = useMemo(
    () => withEmpColumn(columns2, isAllEmpSearch),
    [columns2, isAllEmpSearch]
  );

  // 툴바는 useImperativeHandle 이 노출한 함수만 활성화한다(TabToolbar.hasHandle).
  // 제출 후에는 onDelete 를 아예 내보내지 않아 삭제 버튼이 비활성 상태로 렌더된다.
  // 담당자 미지정 조회에서는 신규·저장도 내보내지 않는다(대상 일지가 특정되지 않는다).
  useImperativeHandle(ref, () => ({
    onSearch,
    ...(isViewOnly ? {} : { onInsert, onSave }),
    ...(isLocked ? {} : { onDelete }),
  }));

  const getChanges = () => ({
    actual: gridref1.current?.getChange?.() ?? emptyChange,
    plan: gridref2.current?.getChange?.() ?? emptyChange,
  });

  const hasChanges = () => {
    const { actual, plan } = getChanges();
    // 드래그 재정렬(no_seq_sort 변경)도 modifiedRowcount(update)에 포함되므로 그것만 본다.
    return actual.modifiedRowcount > 0 || plan.modifiedRowcount > 0;
  };

  /**
   * 조회된 헤더 내용을 검색패널에 되비춘다.
   * 담당자·업무영역·상태는 이 화면에 입력칸이 따로 없고 검색패널이 곧 표시 자리라,
   * 조회 결과를 여기로 되돌리지 않으면 등록된 값이 화면 어디에도 안 보인다.
   * 헤더 값이 비어 있으면 사용자가 넣은 조회조건을 그대로 둔다.
   * (기준주차는 되돌리지 않는다 — 조회조건 자체이고 SnYearWeekPicker 는 마운트 값만 쓴다.)
   */
  const syncSearchPanel = (header?: any) => {
    const search = searchRef.current?.getFormdata?.();
    if (!search) return;
    // 담당자·업무영역을 비운 채로 조회한 것은 "전체 보기" 의도다 —
    // 조회된 일지의 값으로 되채우지 않는다(비운 칸은 비운 채로 둔다).
    const clearedEmp = !search.cd_emp;
    const clearedDomain = !search.fg_domain;
    searchRef.current?.setFormdata?.({
      ...search,
      cd_emp: clearedEmp ? "" : header?.cd_emp || search.cd_emp,
      nm_emp: clearedEmp ? "" : header?.nm_emp || search.nm_emp,
      fg_domain: clearedDomain ? "" : header?.fg_domain || search.fg_domain,
      st_journal: ST_LABELS[header?.st_journal ?? ""] ?? "",
    });
  };

  /**
   * 주간요약(freeform3)이 쓰는 헤더 데이터.
   * 라인 폼과 같은 dc_editcon 식("'st_header' != '1'")을 쓰도록 헤더에도 st_header 를 얹는다.
   * data prop 이 매 렌더 새 객체면 FreeForm 이 입력 중이던 값을 되돌리므로 headerData 기준으로 고정한다.
   */
  const summaryData = useMemo(() => {
    if (!headerData) return headerData;
    // 담당자 미지정 조회는 보기 전용 — 라인 폼과 같은 방식으로 제출 상태 코드를 실어 잠근다.
    const st = isViewOnly ? ST_SUBMIT : headerData.st_journal ?? "";
    return { ...headerData, st_journal: st, st_header: st };
  }, [headerData, isViewOnly]);

  /**
   * 라인 행에 헤더 상태 코드를 얹어 준다 — dc_editcon 이 "'st_journal' == '3'" 처럼 잠금 조건을 쓸 수 있게 한다.
   * 라인 자체의 st_journal 은 조회 SQL이 표시용 이름("제출")으로 내려주는 자리라 판단 근거가 못 되므로 코드로 덮는다.
   * st_header 는 기존 메타 호환용으로 같이 남긴다(저장 payload 에서는 GRID_KEYS 로 제거된다).
   */
  const withHeaderState = (
    rows: any[] | undefined,
    header: any,
    readOnly = false
  ) => {
    // 상세폼 잠금은 메타의 dc_editcon( 'st_journal' == '3' )이 결정한다.
    // 담당자 미지정 조회는 보기 전용이므로 제출과 같은 상태 코드를 실어 폼도 함께 잠근다.
    const st = readOnly ? ST_SUBMIT : header?.st_journal ?? "";
    return (rows ?? []).map((row) => ({
      ...row,
      st_journal: st,
      st_header: st,
      // dc_editcon 은 키 이름을 값으로 치환해 평가하므로 null 이면 문자열 "null" 이 되어
      // "'no_issue' != ''" 같은 식이 항상 참이 된다. 빈 문자열로 정규화해 둔다.
      no_issue: row.no_issue ?? "",
    }));
  };

  /**
   * 조회조건(기준주차·담당자·업무영역)으로 등록된 헤더를 찾는다. 없으면 null.
   * 일지는 주차·담당자·업무영역당 한 건이라, 업무영역이 다르면 다른 일지다.
   */
  const fetchHeader = async (search: any) => {
    try {
      const data: any = await fnGetDataSet({
        id: QUERY_ID,
        params: search,
      });
      return data?.[0]?.[0] ?? null;
    } catch (error) {
      return null;
    }
  };

  /**
   * 화면에 열려 있는 일지가 지금 조회조건과 같은 주차·담당자의 것인지.
   * 헤더 일자(dt)는 늘 기준주차의 월요일로 저장되므로(buildHeaderRow) 그 값으로 주차를 비교한다.
   */
  const isSameJournalWeek = (header: any, search: any) =>
    !!header?.dt &&
    header.dt === weekMonday(search.yy, search.no_week) &&
    String(header.cd_emp ?? "") === String(search.cd_emp ?? "");

  /**
   * 저장 대상 헤더(없으면 null → 신규 채번).
   * 1) 같은 업무영역으로 등록된 건이 있으면 그 건.
   * 2) 없으면 화면에 열려 있는 "업무영역 미정" 건을 이어받는다.
   *    업무영역을 정하지 않고 저장한 뒤 나중에 정하는 흐름에서, 새로 채번하면
   *    같은 내용이 미정 건과 새 건 두 개로 갈라진다.
   *    (다른 업무영역으로 새로 쓰려면 신규(+)를 누른다 — headerRef 가 비워져 이어받지 않는다.)
   */
  const findSaveTargetHeader = async (search: any) => {
    const sameDomain = await fetchHeader(search);
    if (sameDomain) return sameDomain;

    const opened = headerRef.current;
    if (
      opened?.no_journal &&
      !opened.fg_domain &&
      isSameJournalWeek(opened, search)
    ) {
      return opened;
    }
    return null;
  };

  //#region onSearch
  const onSearch = async (skipDirtyCheck = false) => {
    // 담당자·업무영역 등 필수값은 sys_formctrl.yn_required='Y' 로 선언하고
    // 검색패널 공통 검증(표준 메시지)으로 막는다. 전체 보기 없음.
    if (!searchRef.current?.requiredValuesCheck?.()) return false;
    // 저장 직후 재조회는 방금 저장했으므로 변경분 확인을 건너뛴다.
    if (!skipDirtyCheck && hasChanges()) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm === undefined) return;
      if (confirm && !(await onSave(false))) return;
    }

    userEdited.current = false;
    try {
      // 0: 헤더(주차), 1: 금주실적(fg_plan=1), 2: 차주계획(fg_plan=2)
      const search = searchRef.current?.getFormdata?.() ?? {};
      const data: any = await fnGetDataSet({
        id: QUERY_ID,
        params: search,
      });

      const header = data?.[0]?.[0];
      headerRef.current = header ?? null;
      // 헤더가 없으면 조회조건(주차/담당자/업무영역)을 그대로 신규 헤더의 기본값으로 쓴다.
      // 다만 상태는 표시용 이름이 들어 있어 코드로 되돌린다.
      setHeaderData(header ?? { ...search, st_journal: "" });
      // 담당자를 비우고 조회하면 여러 사람의 일지가 섞이니 그리드에 담당자 컬럼을 켠다.
      setIsAllEmpSearch(!search.cd_emp);
      syncSearchPanel(header);
      // 업무영역이 바뀌면 그리드 컬럼·상세폼 구성이 달라진다(개발 = 이슈 기반).
      applyDomainMeta(header?.fg_domain ?? search.fg_domain);

      gridref1.current?.resetUpdate();
      gridref2.current?.resetUpdate();
      // 편집 가능 조건(dc_editcon)은 그 행의 데이터로 평가된다.
      // 라인이 내려주는 st_journal 은 표시용 이름이라, 헤더 상태 코드로 덮어 실어 보낸다.
      // 상세폼 잠금 판단은 state(isViewOnly)가 아직 갱신되기 전이라 여기서 같은 규칙으로 계산한다.
      // 담당자를 비웠거나(전체 보기) 조회된 일지의 담당자가 내가 아니면 보기 전용이다.
      const owner = String(header?.cd_emp ?? search.cd_emp ?? "");
      const readOnly = !search.cd_emp || (!!myEmp && owner !== myEmp);
      setDataSource1(withHeaderState(data?.[1], header, readOnly));
      setDataSource2(withHeaderState(data?.[2], header, readOnly));
      // 상세폼을 비웠다가 채우면 그 사이 첫 컨트롤(제목)이 빈 값인 채로 blur 되고,
      // SnTextBox 의 blur 콜백이 그 빈 값을 포커스 행에 되써서 제목이 지워진다.
      // (좌우 폼이 서로 포커스를 빼앗아 blur 가 반드시 한 번 발생한다.)
      // 결과가 있으면 그리드가 0행 포커스로 폼을 다시 채우므로 여기서 비우지 않는다.
      if (!data?.[1]?.length) setFocusedRow1(undefined);
      if (!data?.[2]?.length) setFocusedRow2(undefined);
      // 이후 업무영역을 바꾸면 자동으로 다시 조회한다.
      searched.current = true;
      return true;
    } catch (error: any) {
      return false;
    }
  };
  // 업무영역 변경 시 자동 조회가 항상 최신 onSearch 를 쓰도록 매 렌더마다 갈아 끼운다.
  onSearchRef.current = onSearch;
  //#endregion

  //#region onInsert / onDelete
  /**
   * 새 라인의 기본값.
   * 조회조건을 통째로 넣지 않는다 — yy/no_week/cd_emp/nm_emp/st_journal 이 라인 컬럼을 덮어써서
   * 관련자 이름이 담당자 이름으로 바뀌거나 라인 상태에 "작성중" 같은 표시용 문구가 저장된다.
   */
  const newRowDefaults = (fg_plan: string) => ({
    cd_firm: userInfo.cd_firm,
    fg_plan,
    // 직접입력 행의 일자는 당일. (이슈 가져오기는 issueToLine 이, 실적이월은 원본 행이 dt 를 덮는다.)
    dt: dayjs().format("YYYYMMDD"),
    no_priority: NO_PRIORITY_NORMAL,
    // 신규 행도 헤더 상태 코드를 실어야 dc_editcon 이 조회된 행과 같은 기준으로 동작한다.
    st_journal: headerRef.current?.st_journal ?? ST_NEW,
    st_header: headerRef.current?.st_journal ?? ST_NEW,
    // 직접 추가한 행은 이슈와 무관하다. 빈 문자열로 둬야 dc_editcon 의 "'no_issue' != ''" 가 거짓이 된다.
    no_issue: "",
    // 표시용. 조회 SQL 이 coalesce 로 넣어 주지만 재조회 전까지는 화면이 비어 보인다.
    nm_issue_disp: "직접입력",
  });

  /**
   * 신규(툴바 +) = 조회조건(기준주차·담당자·업무영역)으로 새 주간일지 작성 시작.
   * 조회조건은 건드리지 않는다 — 그 값이 곧 만들 일지의 키다.
   * 그 조건으로 이미 등록된 일지가 있으면 알리고 등록분을 보여준다. 없으면 상태를 작성중으로 두고 시작한다.
   * 행 추가는 각 그리드 제목줄의 + 로 한다.
   */
  const onInsert = async () => {
    if (hasChanges()) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm === undefined) return;
      if (confirm && !(await onSave(false))) return;
    }

    // 기준주차·담당자·업무영역은 사용자가 고른 값을 그대로 둔다.
    // 이 셋이 곧 "어떤 일지를 만들 것인가"의 입력값이다(buildHeaderRow 참고).
    // 담당자를 로그인 사용자로 덮으면 다른 담당자의 일지를 시작할 수 없고,
    // 로그인 사용자에게 그 주차 일지가 이미 있으면 "등록된 데이터가 존재합니다"로 막혀 버린다.
    // 비어 있을 때만 로그인 사용자로 채운다.
    const search = searchRef.current?.getFormdata?.() ?? {};
    searchRef.current?.setFormdata?.({
      ...search,
      cd_emp: search.cd_emp || userInfo.cd_emp || "",
      nm_emp: search.nm_emp || userInfo.nm_emp || "",
      st_journal: ST_LABELS[ST_NEW],
    });

    // 조회된 일지와의 연결을 끊어 다음 저장이 insert(신규 채번)로 가게 한다.
    headerRef.current = null;
    // 여기서 변경분을 비워야 이어지는 조회가 "저장하시겠습니까?"를 다시 묻지 않는다.
    gridref1.current?.resetUpdate();
    gridref2.current?.resetUpdate();
    setDataSource1([]);
    setDataSource2([]);
    setFocusedRow1(undefined);
    setFocusedRow2(undefined);

    // 초기화한 주차에 이미 등록된 일지가 있으면 신규로 넘어가지 않고 등록분을 보여준다.
    if (!(await onSearch())) return;
    if (headerRef.current) {
      await showDialog("alter", "등록된 데이터가 존재합니다.");
      return;
    }

    // 신규 작성 시작 — 조회조건에서 만든 기본값은 두고 상태만 신규(1)로 세운다.
    setHeaderData((prev: any) => ({ ...(prev ?? {}), st_journal: ST_NEW }));
    syncSearchPanel({ st_journal: ST_NEW });
  };

  /**
   * 그리드 제목줄 휴지통 = 선택 행 삭제 + 상세폼 정리.
   * 남은 행이 있으면 그리드가 포커스를 옮기면서 onFocusedRowChanged 로 폼을 다시 채우지만,
   * 마지막 행을 지우면 그 이벤트가 없어 상세폼이 삭제된 행의 값을 계속 들고 있다.
   * userEdited 도 같이 내린다 — 휴지통 클릭이 "사용자 편집"으로 잡힌 상태에서 폼이 비워지면
   * SnTextBox 의 blur 가 그 빈 값을 남은 행에 되쓸 수 있다.
   */
  const onDeleteRow = (gridref: any, setFocusedRow: (row: any) => void) => {
    gridref.current?.deleteSelectedRows();
    userEdited.current = false;
    if (!gridref.current?.getFilteredData?.()?.length) {
      setFocusedRow(undefined);
    }
  };

  // 상단 툴바 삭제 = 현재 조회된 주간일지 전체 삭제(헤더 + 금주실적 + 차주계획).
  // 개별 행 삭제는 각 그리드 제목줄의 휴지통(onBtnDeleteClick)이 담당한다.
  const onDelete = async () => {
    // 제출/완료 상태는 삭제 불가. 툴바가 비활성으로 다시 그려지기 전에 눌릴 수 있어
    // (ref 변경만으로는 리렌더가 안 일어난다) 버튼 상태와 무관하게 여기서 한 번 더 막는다.
    if (isLocked) {
      await showDialog("alter", "제출된 주간일지는 삭제할 수 없습니다.");
      return;
    }
    // 저장된 일지만 삭제 대상 — 조회된 헤더의 업무번호가 있어야 한다.
    const header = headerRef.current ?? headerData;
    if (!header?.no_journal) {
      await showDialog("alter", "삭제할 주간일지가 없습니다.");
      return;
    }
    const confirm = await showDialog(
      "confirm",
      "현재 주간일지(금주실적·차주계획 전체)를 삭제하시겠습니까?"
    );
    if (!confirm) return;

    // 저장된 라인(업무번호가 매겨진 행)만 삭제 대상. 미저장 신규 행은 DB에 없어 제외한다.
    // 참조업무는 저장 때처럼 두 컬럼으로 되돌린다(splitRef).
    const lineDeletes = [...dataSource1, ...dataSource2]
      .filter((row) => row.no_journal)
      .map((row) => splitRef(stripGridKeys(row)));

    const ok = await fnSave("SN_SFA_JOURNAL_REG_001_SAVE_01", {
      sfa_workjournalh: {
        ...emptyChange,
        delete: [stripGridKeys(header)],
        modifiedRowcount: 1,
      },
      sfa_workjournall: {
        ...emptyChange,
        delete: lineDeletes,
        modifiedRowcount: lineDeletes.length,
      },
    });
    if (!ok) return;

    // 삭제 후 화면을 비우고 재조회한다(그 주차엔 이제 일지가 없다).
    headerRef.current = null;
    gridref1.current?.resetUpdate();
    gridref2.current?.resetUpdate();
    await onSearch();
  };
  //#endregion

  /**
   * 저장/제출이 공유하는 헤더 행.
   * 담당자·업무영역은 이 화면에 입력칸이 따로 없고 조회조건이 곧 입력값이라, 헤더가 비어 있으면 조회조건에서 채운다.
   * 값이 이미 있는 일지는 조회조건을 바꿔도 덮지 않는다(다른 담당자·업무영역의 일지로 바뀌어 버린다).
   * 업무영역이 비어 있는 "미정" 건만 조회조건에서 채워진다 — 나중에 정한 업무영역이 그 건에 반영되는 통로다.
   */
  const buildHeaderRow = (extra: any = {}, base: any = headerData) => {
    const search = searchRef.current?.getFormdata?.() ?? {};
    return stripGridKeys({
      ...(base ?? {}),
      ...(summaryRef.current?.getFormdata?.() ?? {}),
      cd_firm: userInfo.cd_firm,
      cd_emp: base?.cd_emp || search.cd_emp || userInfo.cd_emp || "",
      fg_domain: base?.fg_domain || search.fg_domain || "",
      yy: base?.yy || search.yy || "",
      no_week: base?.no_week || search.no_week || "",
      // 헤더 일자는 기준주차의 월요일. 채번 기준일과 같아야 번호의 연월이 주차와 맞는다.
      dt: base?.dt || weekMonday(search.yy, search.no_week),
      ...extra,
    });
  };

  //#region onSave
  // 저장 후 재조회 시 다시 포커스할 그리드(마지막으로 포커스된 쪽).
  const lastFocusedGrid = useRef<"1" | "2">("1");
  // 저장 직후 재조회에서 다시 포커스할 대상. 그리드의 onRowDataReady(0행 포커스 후 호출)에서 소비한다.
  const pendingRefocus = useRef<{ grid: "1" | "2"; row: any } | null>(null);

  // 방금 편집/추가한 행으로 포커스를 되돌린다(그리드가 로드 후 0행에 준 포커스를 덮는다).
  const refocusRow = (gridKey: "1" | "2", row: any) => {
    const grid = (gridKey === "2" ? gridref2 : gridref1).current;
    if (!grid || !row) return;
    const total = grid.getDisplayedRowCount?.() ?? 0;
    if (!total) return;
    let idx = total - 1; // 기본값: 마지막 행(새로 추가한 행)
    const noLine = row.no_line;
    const title = row.dc_title;
    for (let i = 0; i < total; i++) {
      const d = grid.getRowNodeAtIndex?.(i)?.data;
      if (!d) continue;
      // 기존 행: 항번(no_line), 신규 행(항번 없음): 제목으로 매칭
      if (noLine != null && String(d.no_line) === String(noLine)) {
        idx = i;
        break;
      }
      if (noLine == null && title && d.dc_title === title) {
        idx = i;
        break;
      }
    }
    // 선택 하이라이트 해제/재선택 + 포커스 + 스크롤을 그리드 공통 로직으로(0행 선택이 안 남게).
    grid.selectAndFocusRow?.(idx);
  };

  // 재조회 완료(그리드 0행 포커스 직후) 시점 — 저장한 행이 있으면 그 행으로 다시 포커스.
  const handleRowDataReady = (gridKey: "1" | "2") => {
    const p = pendingRefocus.current;
    if (!p || p.grid !== gridKey) return;
    pendingRefocus.current = null;
    refocusRow(gridKey, p.row);
  };
  const onRowDataReady1 = () => handleRowDataReady("1");
  const onRowDataReady2 = () => handleRowDataReady("2");

  const onSave = async (reloadAfterSave = true) => {
    try {
      // 남의 일지·담당자 미지정 조회는 대상이 내 일지가 아니다(툴바에서도 빠지지만 다른 경로 대비 가드).
      if (isViewOnly) {
        await showDialog("alter", "본인 담당 일지만 저장할 수 있습니다.");
        return false;
      }
      if (
        !gridref1.current?.requiredValuesCheck?.() ||
        !gridref2.current?.requiredValuesCheck?.()
      ) {
        return false;
      }

      const { actual, plan } = getChanges();

      // 신규행 정렬순번(no_seq_sort) 부여 — 드래그로 이미 값이 있으면 유지, 없으면 그리드(fg_plan)별 max+1.
      // (드래그는 SnGrid 가 no_seq_sort 를 채우고, 드래그 안 한 신규행만 여기서 뒤에 붙인다.)
      const assignSeqSort = (gridref: any, change: any) => {
        const all: any[] = gridref.current?.getRowData?.() ?? [];
        let max = all.reduce(
          (m: number, r: any) => Math.max(m, Number(r?.no_seq_sort) || 0),
          0
        );
        (change.insert ?? []).forEach((r: any) => {
          if (!(Number(r.no_seq_sort) > 0)) {
            max += 1;
            r.no_seq_sort = max;
          }
        });
      };
      assignSeqSort(gridref1, actual);
      assignSeqSort(gridref2, plan);

      // 변경 없으면 저장하지 않는다 — 라인(추가/수정/삭제·재정렬) 또는 주간요약(FreeForm) 변경이 있어야 한다.
      // FreeForm.getChangeData()는 로드 시 undefined 로 초기화되고 사용자가 편집해야 값이 찬다.
      const summaryChanged = !!summaryRef.current?.getChangeData?.();
      if (!hasChanges() && !summaryChanged) {
        // 안내창을 await 하지 않고 즉시 반환해 툴바 스피너(저장 로딩)가 뜨지 않게 한다.
        // (이 가드 앞에는 await 가 없어 onSave 가 동기적으로 끝나므로 로딩이 켜졌다 꺼질 틈이 없다.)
        void showDialog("alter", "수정된 내용이 없습니다.");
        return false;
      }

      // 참조업무는 화면 표시용으로 합쳐져 있으므로 저장 직전에 두 컬럼으로 되돌린다.
      const toRow = (row: any) => splitRef(stripGridKeys(row));
      // 드래그 재정렬(no_seq_sort 변경)은 변경분(update)에 이미 잡혀 있어 그대로 내보낸다.
      const line = {
        insert: [...(actual.insert ?? []), ...(plan.insert ?? [])].map(toRow),
        update: [...(actual.update ?? []), ...(plan.update ?? [])].map(toRow),
        delete: [...(actual.delete ?? []), ...(plan.delete ?? [])].map(toRow),
        modifiedRowcount: actual.modifiedRowcount + plan.modifiedRowcount,
      };

      // 헤더는 주차·담당자·업무영역당 하나뿐이다. 화면 상태만 믿으면 신규 후 주차를 바꿔 저장할 때
      // 중복 헤더가 생기므로, 저장 직전에 그 주차 헤더를 다시 확인한다.
      // 이미 있으면 그 번호에 붙이고(update), 없을 때만 채번한다(insert).
      const search = searchRef.current?.getFormdata?.() ?? {};
      const existingHeader = await findSaveTargetHeader(search);
      let no_journal = existingHeader?.no_journal ?? "";
      if (!no_journal) {
        no_journal =
          (await getNoSlip(
            NO_SLIP_MODULE,
            NO_SLIP_LINE,
            "JURNAL",
            weekMonday(search.yy, search.no_week),
            cd_menu
          )) ?? "";
        if (!no_journal) {
          await showDialog("alter", "업무번호 채번에 실패했습니다. 채번 설정을 확인해 주세요.");
          return false;
        }
      }

      // 저장되면 '신규'는 Draft 로 올린다 — 신규(1)는 아직 저장하지 않은 건을 뜻한다.
      // 제출(3)된 건은 저장만 해도 제출이 풀리면 안 되므로 그대로 둔다.
      const base = existingHeader ?? headerData;
      const headerRow = buildHeaderRow(
        {
          no_journal,
          st_journal: base?.st_journal === ST_SUBMIT ? ST_SUBMIT : ST_DRAFT,
        },
        base
      );
      const header = {
        ...emptyChange,
        [existingHeader ? "update" : "insert"]: [headerRow],
        modifiedRowcount: 1,
      };
      // 라인은 언제나 상단 헤더의 업무번호에 붙는다(항번은 최대값+1 이라 백엔드가 매긴다).
      line.insert = line.insert.map((row: any) => ({ ...row, no_journal }));

      const ok = await fnSave("SN_SFA_JOURNAL_REG_001_SAVE_01", {
        sfa_workjournalh: header,
        sfa_workjournall: line,
      });
      if (!ok) return false;

      // 재조회로 포커스가 초기화되므로, 방금 편집/추가하던 행을 기억해 뒀다가
      // 재조회 완료(그리드 onRowDataReady) 시점에 그 행으로 다시 포커스한다.
      const focusKey = lastFocusedGrid.current;
      const focusRow = focusKey === "2" ? focusedRow2 : focusedRow1;
      if (focusRow) pendingRefocus.current = { grid: focusKey, row: focusRow };

      gridref1.current?.resetUpdate();
      gridref2.current?.resetUpdate();
      if (reloadAfterSave) await onSearch(true);
      return true;
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  //#region 제출 / 제출취소
  /**
   * 같은 버튼이 상태에 따라 제출과 제출취소를 겸한다.
   * 제출취소는 Draft(2)로 되돌린다 — 한 번 작성한 건이라 '신규'가 아니고,
   * 잠금이 풀려 그리드 행추가/삭제와 삭제 버튼이 다시 열린다(isLocked).
   */
  const onSubmit = async () => {
    const nextSt = isSubmitted ? ST_DRAFT : ST_SUBMIT;
    const label = isSubmitted ? "제출취소" : "제출";

    // 버튼도 비활성이지만 다른 호출 경로 대비 가드(내 일지가 아니면 상태를 바꿀 수 없다).
    if (isViewOnly) {
      await showDialog("alter", `본인 담당 일지만 ${label}할 수 있습니다.`);
      return;
    }

    if (hasChanges()) {
      await showDialog("alter", `저장 후 ${label}하세요.`);
      return;
    }
    const confirm = await showDialog(
      "confirm",
      `주간일지를 ${label}하시겠습니까?`
    );
    if (!confirm) return;

    setHeaderData((prev: any) => ({ ...(prev ?? {}), st_journal: nextSt }));
    await fnSave("SN_SFA_JOURNAL_REG_001_SAVE_01", {
      sfa_workjournalh: {
        ...emptyChange,
        update: [buildHeaderRow({ st_journal: nextSt })],
        modifiedRowcount: 1,
      },
      sfa_workjournall: emptyChange,
    });
    await onSearch();
  };
  //#endregion

  //#region 이슈 가져오기
  /**
   * 이슈 → 일지 라인 매핑. **저장되는 건 no_issue 뿐**이고, 제품구분/유형/상태/요청자/납기일은
   * 조회 SQL 이 sfa_issue 조인으로 채운다(이슈가 바뀌면 같이 바뀌어야 하므로 복사 저장하지 않는다).
   * 다만 방금 가져온 행은 아직 재조회 전이라 그리드가 빈칸이 되므로, 팝업이 준 표시값을 실어 준다.
   * 이 표시값은 GRID_KEYS 에 들어 있어 저장 payload 에서 빠진다.
   */
  const issueToLine = (issue: any) => ({
    no_issue: issue.no_issue,
    nm_issue_disp: issue.no_issue,
    dc_title: issue.dc_title ?? "",
    // 일자는 이슈 납기일 우선, 없으면 이슈 등록일. 둘 다 없으면 기준주차 월요일로 둔다.
    dt:
      issue.dt_due ||
      issue.dt_issue ||
      weekMonday(
        searchRef.current?.getFormdata?.()?.yy,
        searchRef.current?.getFormdata?.()?.no_week
      ),
    cd_cust: issue.cd_cust ?? "",
    nm_cust: issue.nm_cust ?? "",
    cd_pjt: issue.cd_pjt ?? "",
    nm_pjt: issue.nm_pjt ?? "",
    yn_issue: "Y",
    // 그리드 담당자 컬럼(nm_emp)에 이슈 담당자명을 실어 import 즉시 보이게 한다.
    // 재조회 전 표시용 — sfa_workjournall DTO 에 없는 키라 저장돼도 무시된다(GRID_KEYS 금지: 헤더 저장 공유).
    nm_emp: issue.nm_emp ?? "",
    // 표시용(저장 제외)
    nm_fg_item: issue.nm_fg_item ?? "",
    nm_tp_issue1: issue.nm_tp_issue1 ?? "",
    nm_tp_issue2: issue.nm_tp_issue2 ?? "",
    nm_st_issue: issue.nm_st_issue ?? "",
    nm_emp_issue: issue.nm_emp ?? "",
    dt_due: issue.dt_due ?? "",
  });

  /** 이슈 가져오기 팝업에서 체크한 이슈를 해당 그리드의 새 행으로 추가한다. */
  const onLoadIssues = async (fg_plan: string) => {
    const gridref = fg_plan === FG_ACTUAL ? gridref1 : gridref2;
    const component = (
      <SN_SFA_JOURNAL_REG_001_POP_02
        searchData={searchRef.current?.getFormdata?.() ?? {}}
      />
    );
    const result = await showResizablePopupModal({
      title: "이슈 가져오기",
      children: component,
      validation: true,
      width: "1000px",
      height: "600px",
    });

    if (!result || result === "close") return;

    // 이미 가져온 이슈는 건너뛴다(같은 이슈를 두 줄로 만들지 않는다).
    const exists = new Set<string>();
    gridref.current?.forEachNode?.((node: any) => {
      if (node?.data?.no_issue) exists.add(String(node.data.no_issue));
    });

    const rows: any[] = result[0] ?? [];
    rows.forEach((row) => {
      const no_issue = String(row.no_issue ?? "");
      if (!no_issue || exists.has(no_issue)) return;
      exists.add(no_issue);
      gridref.current?.addRow(
        {
          // 기본값을 먼저 깔고 이슈 매핑으로 덮는다 — 순서가 바뀌면 newRowDefaults 의
          // no_issue: "" 가 이슈번호를 지워 잠금·저장이 모두 어긋난다.
          ...newRowDefaults(fg_plan),
          ...issueToLine(row),
          // 이슈 우선순위(SFA035)는 일지 긴급도와 같은 분류코드라 그대로 이어받는다.
          no_priority: row.fg_priority ?? row.no_priority ?? NO_PRIORITY_NORMAL,
        },
        true,
        true
      );
    });
  };
  //#endregion

  //#region 전주계획 가져오기 / 실적이월
  /** 전주(前週)에 세운 차주계획을 팝업에서 선택해 금주실적으로 복사한다. 이전 주차 계산은 서버(pop_01_01) 담당. */
  const onLoadPrevPlan = async () => {
    const component = (
      <SN_SFA_JOURNAL_REG_001_POP_01
        searchData={searchRef.current?.getFormdata?.() ?? {}}
      />
    );
    const result = await showResizablePopupModal({
      title: "전주계획가져오기",
      children: component,
      validation: true,
      width: "900px",
      height: "600px",
    });

    if (!result || result === "close") return;

    const rows: any[] = result[0] ?? [];
    rows.forEach((row) => {
      // 원본 키(no_journal/no_line)는 떼고 참조 컬럼(no_journal_ref/no_line_ref)만 남긴다 —
      // 그대로 두면 이번 주차의 같은 항번과 PK가 겹쳐 저장 시 duplicate key 가 난다.
      // 항번은 저장 때 백엔드가 최대값 + 1 로 매긴다.
      const { no_journal, no_line, ...rest } = stripGridKeys(row);
      // 이슈와 연결된 계획행이면 제품구분(nm_fg_item)·유형1/2(nm_tp_issue1/2)·상태(nm_st_issue)·
      // 이슈번호(no_issue/nm_issue_disp)를 이어받는다. stripGridKeys 가 표시 컬럼을 떼므로 원본 row 에서
      // 되살리고, newRowDefaults 의 no_issue:""·nm_issue_disp:"직접입력" 뒤에 둬 덮어쓴다.
      // (직접입력 계획행은 그대로 직접입력으로 둔다.)
      const carriedIssue = rest.no_issue
        ? {
            no_issue: rest.no_issue,
            nm_fg_item: row.nm_fg_item,
            nm_tp_issue1: row.nm_tp_issue1,
            nm_tp_issue2: row.nm_tp_issue2,
            nm_st_issue: row.nm_st_issue,
            nm_issue_disp: row.nm_issue_disp,
          }
        : {};
      gridref1.current?.addRow(
        {
          ...rest,
          no_journal_ref: joinRef(rest.no_journal_ref, rest.no_line_ref),
          ...newRowDefaults(FG_ACTUAL),
          // 가져온 계획의 긴급도는 그대로 이어받는다(신규 기본값으로 덮지 않는다).
          no_priority: rest.no_priority ?? NO_PRIORITY_NORMAL,
          ...carriedIssue,
        },
        true,
        true
      );
    });
  };

  /**
   * 선택한 금주실적을 차주계획 그리드에 새 행으로 추가한다(이월여부 Y).
   * 원본 키(no_journal/no_line)는 떼고 참조 컬럼에만 남긴다 —
   * 그대로 두면 원본과 PK가 같아져 저장 시 충돌한다. 항번은 저장 때 백엔드가 매긴다.
   */
  const onCarryOver = async () => {
    // 행 선택(Ctrl/Shift+클릭)과 셀 범위(마우스 드래그) 둘 다 이월 대상으로 합친다(KeyField 로 중복 제거).
    const byKey = new Map<string, any>();
    [
      ...(gridref1.current?.getSelectedRows?.() ?? []),
      ...(gridref1.current?.getRangeSelectedRows?.() ?? []),
    ].forEach((r: any) => byKey.set(r.KeyField, r));
    const rows = [...byKey.values()];
    if (!rows.length) {
      await showDialog("alter", "이월할 행을 선택하세요.");
      return;
    }
    rows.forEach((row: any) => {
      // stripGridKeys 를 쓰지 않는다 — 그건 저장 payload 용이라 nm_fg_item/nm_tp_issue1·2/
      // nm_st_issue/dt_due/nm_issue_disp(이슈 표시 컬럼)까지 지워, 이월된 차주계획 행이 빈칸이 된다.
      // 여기서는 그리드 런타임 키와 PK/이력만 떼고 이슈 표시 컬럼은 그대로 이월한다.
      // (표시 컬럼은 저장 시 buildLineRows 의 stripGridKeys 에서 다시 걸러진다.)
      const {
        KeyField,
        state,
        block,
        fixCheckbox,
        st_header,
        no_journal,
        no_line,
        cd_user_reg,
        tm_reg,
        cd_user_amd,
        tm_amd,
        ...rest
      } = row;

      gridref2.current?.addRow(
        {
          ...rest,
          cd_firm: userInfo.cd_firm,
          fg_plan: FG_PLAN,
          [COL_CARRY]: "Y",
          no_journal_ref: joinRef(no_journal, no_line),
          no_line_ref: no_line ?? 0,
        },
        true,
        true
      );
    });
  };
  //#endregion

  /**
   * SharedForm 은 값이 바뀔 때마다 gridref.sharedCellValueChanged 로 포커스 행을 통째로 되쓴다.
   * 그런데 조회/행이동 직후 SharedForm 이 스스로 첫 컨트롤에 포커스를 옮기면서
   * (SharedForm.focusInputByIndex) 옆 폼의 SnTextBox 가 blur 되고,
   * SnTextBox.handleBlur 는 아직 새 값으로 갱신되지 않은 내부 state 를 그대로 올려보낸다.
   * → 제목이 빈 값으로 되써져 그리드에서 사라진다(좌우 폼이 서로 포커스를 뺏는 이 화면에서 재현).
   * 공통 컴포넌트는 못 고치므로, 사용자가 실제로 폼을 건드린 뒤의 되쓰기만 통과시킨다.
   */
  const userEdited = useRef(false);
  const markUserEdited = () => (userEdited.current = true);
  const guardedGridRef = (gridref: any) => ({
    get current() {
      const grid = gridref.current;
      if (!grid) return grid;
      return {
        ...grid,
        sharedCellValueChanged: (change: any) => {
          if (!userEdited.current) return;
          grid.sharedCellValueChanged(change);
        },
      };
    },
  });
  const formGridRef1 = useMemo(() => guardedGridRef(gridref1), []);
  const formGridRef2 = useMemo(() => guardedGridRef(gridref2), []);

  const onFocusedRow1Changed = useCallback((row: any) => {
    userEdited.current = false;
    lastFocusedGrid.current = "1";
    setFocusedRow1(row);
  }, []);

  const onFocusedRow2Changed = useCallback((row: any) => {
    userEdited.current = false;
    lastFocusedGrid.current = "2";
    setFocusedRow2(row);
  }, []);

  //#region UI
  return (
    <div
      className="h-full flex flex-col"
      onPointerDownCapture={markUserEdited}
      onKeyDownCapture={markUserEdited}
    >
      {/* 상단: 기준주차 / 담당자 / 업무영역 / 상태 + 제출 */}
      <div className="mb-1 flex">
        <div className="flex-1 min-w-0">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            gridTemplateColumns={SEARCH_COLUMNS}
            onValueChanged={onSearchValueChanged}
          />
        </div>
        {/* SearchPanel 의 구분선(<hr>)은 패널 폭까지만 그려진다. 버튼이 차지한 만큼 선이 끊겨
            아래 그리드 아이콘이 선을 뚫고 올라온 것처럼 보이므로, 같은 선을 여기까지 이어 그린다.
            버튼은 접기화살표(15px)를 뺀 컨트롤 영역 기준으로 가운데 맞춘다. */}
        <div className="flex flex-col">
          <div className="flex-1 flex items-center pl-2 pb-[15px]">
            <SnButton
              text={isSubmitted ? "제출취소" : "제출"}
              onClick={onSubmit}
              // 담당자 미지정 조회는 대상 일지가 특정되지 않는다(isLocked 로 막으면 제출취소까지 막힌다).
              disabled={isViewOnly}
            />
          </div>
          <div style={{ height: "1px", background: "rgb(186,191,199)" }} />
        </div>
      </div>

      {/* 세로 스크롤은 이 컨테이너 하나만 갖는다(화면 오른쪽 끝 1개). 가로는 잠근다. */}
      <div className="flex w-full h-full min-h-0 space-x-1.5 overflow-y-auto overflow-x-hidden">
        {/* 좌측: 금주실적 그리드 + 상세정보 */}
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panelLeft}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={320}
        >
          {/* 금주실적 그리드와 상세는 간격 없이 바로 이어 붙인다. */}
          {/* 자체 스크롤 없음 — 넘치는 만큼 바깥 컨테이너가 스크롤한다.
              flex 대신 block: flex 자식은 공간이 모자라면 줄어들어 스크롤 대신 찌그러진다. */}
          <div className="h-full min-h-0 min-w-0">
            <SnResizable
              direction={"height"}
              seq={2}
              cd_menu={cd_menu}
              defaultSize={defaultSize}
              size={sizes.panelActual}
              onResizeStop={onResizeStop}
              enable={{ bottom: true }}
              minHeight={130}
            >
              <SnGrid
                ref={gridref1}
                columnConfig={gridColumns1}
                rowData={dataSource1}
                etcProps={{
                  // 제출(3) 후에는 셀 편집 자체를 막는다. 신규(1)·Draft(2)는 편집 가능.
                  isEditable: !isLocked,
                  // 실적이월은 여러 건을 한 번에 넘긴다. 드래그(셀 범위)와 Ctrl/Shift + 클릭으로 고른다.
                  rowSelection: "multiple",
                  title: "금주실적",
                  // 조회 시 첫 컬럼 자동 오름차순 정렬 끔 — 저장 행이 최상단으로 튀지 않게 DB 순서 유지.
                  enableInitialSort: false,
                  // 순번 컬럼 드래그로 행 순서 변경 — 놓은 순서대로 no_seq_sort 재부여(공통 SnGrid).
                  // 제출(잠금) 후에는 끈다.
                  rowDragOrderKey: isLocked ? undefined : "no_seq_sort",
                }}
                events={{
                  onFocusedRowChanged: onFocusedRow1Changed,
                  onRowDataReady: onRowDataReady1,
                  onBeforeAddRow: () => newRowDefaults(FG_ACTUAL),
                  // 제출 후에는 핸들러를 넘기지 않는다. SnTabHeader 는 핸들러가 있을 때만
                  // +/삭제 아이콘을 렌더하므로(비활성 표시 옵션이 없다) 버튼 자체가 사라진다.
                  onBtnAddClick: isLocked
                    ? undefined
                    : () => gridref1.current?.addRow(),
                  onBtnDeleteClick: isLocked
                    ? undefined
                    : () => onDeleteRow(gridref1, setFocusedRow1),
                }}
                header={
                  <div
                    style={{
                      height: GRID_HEADER_HEIGHT,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {/* 둘 다 행을 새로 만드는 동작이라 제출 후에는 막는다. */}
                    <SnButton
                      text={"전주계획 가져오기"}
                      onClick={onLoadPrevPlan}
                      disabled={isLocked}
                    />
                    {isDev && (
                      <SnButton
                        text={"이슈 가져오기"}
                        onClick={() => onLoadIssues(FG_ACTUAL)}
                        disabled={isLocked}
                      />
                    )}
                    <SnButton
                      text={"실적이월"}
                      onClick={onCarryOver}
                      disabled={isLocked}
                    />
                  </div>
                }
              />
            </SnResizable>

            {/* fitContent: 높이를 고정하지 않고 폼 내용만큼 늘려 상세 안쪽 스크롤을 없앤다.
                넘치는 만큼은 바깥 컨테이너가 스크롤한다. */}
            <SnResizable
              direction={"height"}
              seq={3}
              cd_menu={cd_menu}
              defaultSize={defaultSize}
              size={sizes.panelActualDetail}
              onResizeStop={onResizeStop}
              fitContent
            >
              <SharedForm
                ref={detailRef1}
                data={focusedRow1}
                controls={detailForm1}
                gridTemplateColumns={FORM_COLUMNS}
                gridref={formGridRef1}
                onValueChaged={onDetailValueChanged}
                autoFocus={false}
              />
            </SnResizable>
          </div>
        </SnResizable>

        {/* 우측: 차주계획 그리드 + 상세정보 + 주간요약 */}
        <div className="flex-1 h-full min-h-0 min-w-0">
          <SnResizable
            direction={"height"}
            seq={4}
            cd_menu={cd_menu}
            defaultSize={defaultSize}
            size={sizes.panelPlan}
            onResizeStop={onResizeStop}
            enable={{ bottom: true }}
            minHeight={130}
          >
            <SnGrid
              ref={gridref2}
              columnConfig={gridColumns2}
              rowData={dataSource2}
              etcProps={{
                // 제출(3) 후에는 셀 편집 자체를 막는다. 신규(1)·Draft(2)는 편집 가능.
                isEditable: !isLocked,
                rowSelection: "single",
                title: "차주계획",
                // 조회 시 첫 컬럼 자동 오름차순 정렬 끔 — 저장 행이 최상단으로 튀지 않게 DB 순서 유지.
                enableInitialSort: false,
                // 순번 컬럼 드래그로 행 순서 변경 — 놓은 순서대로 no_seq_sort 재부여(공통 SnGrid).
                // 제출(잠금) 후에는 끈다.
                rowDragOrderKey: isLocked ? undefined : "no_seq_sort",
              }}
              events={{
                onFocusedRowChanged: onFocusedRow2Changed,
                onRowDataReady: onRowDataReady2,
                onBeforeAddRow: () => newRowDefaults(FG_PLAN),
                onBtnAddClick: isLocked
                  ? undefined
                  : () => gridref2.current?.addRow(),
                onBtnDeleteClick: isLocked
                  ? undefined
                  : () => onDeleteRow(gridref2, setFocusedRow2),
              }}
              header={
                <div
                  style={{
                    height: GRID_HEADER_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {isDev && (
                    <SnButton
                      text={"이슈 가져오기"}
                      onClick={() => onLoadIssues(FG_PLAN)}
                      disabled={isLocked}
                    />
                  )}
                </div>
              }
            />
          </SnResizable>

          {/* 좌측 상세와 동일 — fitContent 로 높이 고정 없음, 폼 안쪽 스크롤 없음. */}
          <SnResizable
            direction={"height"}
            seq={5}
            cd_menu={cd_menu}
            defaultSize={defaultSize}
            size={sizes.panelPlanDetail}
            onResizeStop={onResizeStop}
            fitContent
          >
            <SharedForm
              ref={detailRef2}
              data={focusedRow2}
              controls={detailForm2}
              gridTemplateColumns={FORM_COLUMNS}
              gridref={formGridRef2}
              onValueChaged={onDetailValueChanged}
              autoFocus={false}
            />
          </SnResizable>

          {/* 좌측 그리드 헤더(버튼 2개)만큼 우측이 짧아져 아래가 어긋난다.
              주간요약 위에 그만큼 여백을 줘서 좌우 바닥을 맞춘다. */}
          <div style={{ paddingTop: SUMMARY_TOP_GAP }}>
            {/* fitContent: 주간요약도 내용 높이로 고정한다. % 로 두면 창 높이마다 늘었다 줄어들어
                좌우 컬럼 바닥선을 맞출 수가 없다(좌측은 상세폼이 내용 높이로 고정돼 있다). */}
            <SnResizable
              direction={"height"}
              seq={6}
              cd_menu={cd_menu}
              defaultSize={defaultSize}
              size={sizes.panelWeeklyRecap}
              onResizeStop={onResizeStop}
              fitContent
            >
              {/* 주간총평 / 주요이슈 / 지원요청 / 중점사항 (헤더 1건) */}
              <FreeForm
                ref={summaryRef}
                data={summaryData}
                controls={summaryForm}
                title="주간요약"
                // 화면 맨 아래 폼이라 조회·신규마다 포커스를 가져가면 화면이 아래로 끌려 내려간다.
                autoFocus={false}
              />
            </SnResizable>
          </div>
        </div>
      </div>
    </div>
  );
  //#endregion
});

export default SN_SFA_JOURNAL_REG_001;
